import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import {
  PendingSurvey,
  SurveyResponseValue,
} from '../dialogs/survey-popover/survey.types';

/**
 * Abstract contract for the survey service.
 *
 * Consumers depend on this interface (via the {@link SURVEY_SERVICE} DI token)
 * instead of the concrete `PosthogService` implementation. This keeps consumer
 * code free of vendor-specific concerns and allows the underlying provider to
 * be swapped (e.g. mocked in tests, replaced with another analytics vendor)
 * without touching components.
 */
export interface SurveyServiceInterface {
  /** Currently-active survey + its super-properties, or `null` when nothing is shown. */
  readonly activeSurvey$: Observable<PendingSurvey | null>;

  /**
   * Push a survey definition to the active-survey stream so the popover renders it.
   *
   * @param surveyId             Survey ID in the underlying analytics provider.
   * @param superProps           Extra properties attached to every event for this survey.
   * @param options.ignoreTargeting  Look up the survey in the full project list instead of
   *                                 the user's currently-eligible list. For testing only.
   */
  showSurvey(
    surveyId: string,
    superProps?: Record<string, unknown>,
    options?: { ignoreTargeting?: boolean },
  ): void;

  /** Fire the `survey shown` analytics event for the given pending survey. */
  markSurveyShown(pending: PendingSurvey): void;

  /**
   * Capture collected responses (`survey sent`) and close the popover. Builds
   * the `$survey_response` / `$survey_response_<n>` payload expected by the
   * provider.
   */
  submitSurveyResponse(
    pending: PendingSurvey,
    responses: Record<string, SurveyResponseValue>,
  ): void;

  /** Fire the `survey dismissed` event and close the popover. */
  dismissSurvey(pending: PendingSurvey): void;

  /**
   * Convenience trigger for the CSAT survey on the course-overview page.
   * Resolves the user's federal state from their ZIP and delegates to
   * {@link showSurvey}.
   */
  validateDisplaySurvey(
    survey_type: string,
    page_type: string,
    id: string,
    urlTree: string,
    zipCode: string,
    ignoreTargeting?: boolean,
  ): Observable<unknown>;
}

/**
 * DI token for the survey service. Bind to a concrete implementation in a
 * module's `providers`, e.g.:
 *
 *     { provide: SURVEY_SERVICE, useExisting: PosthogService }
 */
export const SURVEY_SERVICE = new InjectionToken<SurveyServiceInterface>(
  'SurveyService',
);
